import type React from "react";
import { Fragment, memo, useMemo } from "react";
import type { Comment, CommenterDefinition } from "../../types/core";
import { getMergedCommenterMap } from "../../utils/commenter-utils";
import { formatTime } from "../../utils/date-formatter";
import { useSmartScroll } from "../hooks/useSmartScroll";
import { UI_ICONS } from "../icons";

interface CommentDisplayProps {
	comments: Comment[];
	commenterDefinitions: CommenterDefinition[];
	isLoading: boolean;
	isGenerationEnabled: boolean;
	onCommentClick: (commentId: string) => void;
	onGenerateClick: () => void;
	showTimestamps: boolean;
}

interface CommentItemProps {
	comment: Comment;
	commenterDef?: CommenterDefinition;
	showSeparator: boolean;
	showTimestamps: boolean;
	onCommentClick: (commentId: string) => void;
}

/**
 * Individual comment item component, memoized to prevent unnecessary re-renders.
 */
const CommentItem: React.FC<CommentItemProps> = memo(
	({
		comment,
		commenterDef,
		showSeparator,
		showTimestamps,
		onCommentClick,
	}) => {
		return (
			<Fragment>
				{showSeparator && (
					<div className="flex items-center gap-3 my-4 px-4 animate-in fade-in zoom-in duration-500">
						<div className="flex-1 h-px bg-border-main opacity-30" />
						<span className="text-[10px] text-text-dim font-bold tracking-widest">
							新しいリアクション
						</span>
						<div className="flex-1 h-px bg-border-main opacity-30" />
					</div>
				)}
				{/* biome-ignore lint/a11y/useSemanticElements: Keeping div for styling compatibility */}
				<div
					className={`flex items-center justify-between px-3 py-2 my-0.5 rounded-lg cursor-pointer transition-all duration-300 animate-in fade-in slide-in-from-right-5 hover:scale-[1.02] hover:shadow-md active:scale-95 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none break-words max-w-full
				comment-${comment.commenter}`}
					onClick={() => onCommentClick(comment.id)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							onCommentClick(comment.id);
						}
					}}
					role="button"
					aria-label={`${commenterDef?.name || "AI"}のコメントを詳細表示`}
					tabIndex={0}
					style={{
						backgroundColor: commenterDef?.color || "var(--color-card-bg)",
					}}
				>
					<div className="flex items-center gap-2 flex-1">
						<div className="flex items-center gap-1 shrink-0">
							<span className="mr-1" role="img" aria-hidden="true">
								{commenterDef?.icon || UI_ICONS.COMMENT}
							</span>
							<span className="font-semibold">
								{commenterDef?.name || comment.commenter}
							</span>
						</div>
						<div className="flex items-center gap-2">
							{showTimestamps && (
								<span className="text-[11px] opacity-70 font-light whitespace-nowrap">
									{formatTime(comment.timestamp)}
								</span>
							)}
							<span className="text-white">{comment.content}</span>
						</div>
					</div>
				</div>
			</Fragment>
		);
	},
);

/**
 * CommentDisplay component
 * Optimized with React.memo and pre-processed comments to improve rendering performance.
 */
export const CommentDisplay: React.FC<CommentDisplayProps> = memo(
	({
		comments,
		commenterDefinitions,
		isLoading,
		isGenerationEnabled,
		onCommentClick,
		onGenerateClick,
		showTimestamps,
	}) => {
		const listRef = useSmartScroll<HTMLDivElement>(comments.length);

		// Performance optimization: Use pre-calculated static map to avoid O(N*M) iterations in useMemo
		const commenterMap = useMemo(
			() => getMergedCommenterMap(commenterDefinitions),
			[commenterDefinitions],
		);

		// Performance optimization: Avoid redundant sort if already in order.
		// Chronological streams are usually already sorted, making O(N log N) sort unnecessary.
		// By returning the original 'comments' array when already sorted, we maximize reference stability.
		const sortedComments = useMemo(() => {
			// Fast path: check if the array is already sorted (common for live streams)
			// This reduces O(N log N) to O(N) for already-sorted arrays.
			let isAlreadySorted = true;
			for (let i = 1; i < comments.length; i++) {
				if (
					comments[i].timestamp.getTime() < comments[i - 1].timestamp.getTime()
				) {
					isAlreadySorted = false;
					break;
				}
			}

			return isAlreadySorted
				? comments
				: [...comments].sort(
						(a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
					);
		}, [comments]);

		return (
			<div className="flex flex-col h-full overflow-hidden p-2 bg-bg-main text-text-main font-sans text-sm leading-snug">
				<div
					className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 scrollbar-none"
					ref={listRef}
				>
					{sortedComments.length === 0 ? (
						<div className="h-full flex flex-col items-center justify-center text-center p-6 text-text-dim animate-in fade-in duration-500">
							<span
								className="text-4xl mb-4 opacity-50"
								role="img"
								aria-hidden="true"
							>
								{UI_ICONS.COMMENT}
							</span>
							<p className="font-semibold mb-2">
								リアクションがここに表示されます
							</p>
							<p className="text-xs opacity-70 leading-relaxed max-w-[200px]">
								「コメント生成」をクリックすると、AIが内容に応じてリアクションします
							</p>
						</div>
					) : (
						sortedComments.map((comment, index) => {
							// UI-only state like separators is calculated during render to maintain
							// item reference stability and avoid unnecessary object allocations in useMemo.
							const prevTs =
								index > 0 ? sortedComments[index - 1].timestamp.getTime() : 0;
							const currentTs = comment.timestamp.getTime();
							// Use 5000ms (5s) threshold for showing "New Reactions" separator
							const showSeparator = prevTs !== 0 && currentTs - prevTs > 5000;

							// Efficient O(1) lookup for commenter definition
							const commenterDef = commenterMap.get(comment.commenter);

							return (
								<CommentItem
									key={comment.id}
									comment={comment}
									commenterDef={commenterDef}
									showSeparator={showSeparator}
									showTimestamps={showTimestamps}
									onCommentClick={onCommentClick}
								/>
							);
						})
					)}
				</div>
				<div className="p-4 border-t border-border-main flex justify-center bg-card-bg">
					<button
						type="button"
						className={`w-full py-3 rounded-lg border border-primary text-primary font-semibold transition-all duration-200 flex items-center justify-center gap-2 hover:bg-primary hover:text-white focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:border-border-main disabled:text-text-dim
						${isLoading ? "relative" : ""}`}
						disabled={isLoading || !isGenerationEnabled}
						onClick={onGenerateClick}
						title={
							!isGenerationEnabled
								? "発言してから生成してください"
								: "コメントを生成 (Alt+G)"
						}
						aria-busy={isLoading}
					>
						{isLoading ? (
							<span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
						) : (
							<span role="img" aria-hidden="true">
								{UI_ICONS.REGENERATE}
							</span>
						)}
						{isLoading ? "生成中..." : "コメント生成"}
						{!isLoading && (
							<kbd className="hidden md:inline-flex ml-2 px-1.5 py-0.5 text-[10px] bg-white/20 rounded font-mono border-none">
								Alt+G
							</kbd>
						)}
					</button>
				</div>
			</div>
		);
	},
);
